import { LessonSidebarProps, Section } from '@atlassian/learning-components';
import { Location, NavigateFunction, Params } from 'react-router-dom';

import { ScrollBehaviorTypes, setScrollBehaviour } from 'src/utils/lessonScrollBehavior';
import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { ProgressForLessons, LearningProgressStatus } from 'src/services/http/progress';
import { getSlug } from 'src/utils/slug';
import { getLessonUrl, withPrefix } from 'src/utils/getUrl';
import { getLearningPathsHubPart } from 'src/utils/pathUtils';

interface HandleNavigationParams {
  options: { [key: string]: string };
  location: Location;
  params: Readonly<Params<string>>;
  course: ContentfulSmCourse;
  navigate: NavigateFunction;
  scrollToSection: (sectionSlug: string) => void;
}

export const getSectionList = (
  lesson: ContentfulSmCourse['lessonsCollection']['items'][0],
  onNavigation: (options: { [key: string]: string }) => void,
  progress: ProgressForLessons | undefined,
): Section[] => {
  const lessonSlug = getSlug(lesson);

  const lessonProgress = progress?.lessons.find((p) => p.lessonId === lesson.sys.id);
  const sections = lesson.sectionListCollection.items
    .filter((section) => section.title)
    .map((section) => ({
      sectionTitle: section.title,
      slug: section.id,
      lessonSlug,
      isCompleted:
        lessonProgress?.status === LearningProgressStatus.COMPLETED ||
        lessonProgress?.section?.find((p) => p.sectionId === section.sys.id)?.status ===
          LearningProgressStatus.COMPLETED,
      onNavigation,
    }));

  sections.push({
    sectionTitle: 'Quiz',
    slug: 'quiz',
    lessonSlug,
    isCompleted: lessonProgress?.status === LearningProgressStatus.COMPLETED,
    onNavigation,
  });

  return sections;
};

export const getLessonList = (
  course: ContentfulSmCourse,
  onNavigation: (options: { [key: string]: string }) => void,
  currentLessonSlug: string,
  progress: ProgressForLessons | undefined,
): LessonSidebarProps['lessons'] =>
  course.lessonsCollection.items.map((lesson) => {
    const sections = getSectionList(lesson, onNavigation, progress);
    const lessonSlug = getSlug(lesson);

    return {
      lessonTitle: lesson.title,
      slug: lessonSlug,
      duration: `${lesson.duration} MIN`,
      sections,
      isCurrentLesson: currentLessonSlug === lessonSlug,
      isCompleted:
        progress?.lessons.find((p) => p.lessonId === lesson.sys.id)?.status === LearningProgressStatus.COMPLETED,
      onNavigation,
    };
  });

export const handleNavigation = ({
  options,
  location,
  params,
  course,
  navigate,
  scrollToSection,
}: HandleNavigationParams): void => {
  const lessonSlug = options.lessonSlug;
  const sectionSlug = options.sectionSlug && options.sectionSlug.replace(' ', '-');
  const isLessonSectionClicked = !!sectionSlug;
  const prevLocationState = !location.state ? { lessonSlug: '' } : { lessonSlug: location.state.lessonSlug };
  const isCurrentLessonClicked = window.location.pathname.includes(lessonSlug);

  const learningPathSlug = params?.pathId;
  const allLearningPathsHubPart = getLearningPathsHubPart(location.pathname);

  if (!isCurrentLessonClicked || (!isCurrentLessonClicked && !isLessonSectionClicked)) {
    setScrollBehaviour(ScrollBehaviorTypes.UNSET);
  } else {
    setScrollBehaviour(ScrollBehaviorTypes.SMOOTH);
  }

  const courseSlug = getSlug(course);

  if (isLessonSectionClicked) {
    navigate(
      withPrefix(
        getLessonUrl({
          lessonSlug,
          sectionSlug,
          courseSlug,
          pathSlug: learningPathSlug,
        }),
        allLearningPathsHubPart,
      ),
      {
        state: { ...prevLocationState },
      },
    );
    scrollToSection(sectionSlug);
  } else {
    navigate(
      withPrefix(
        getLessonUrl({
          lessonSlug,
          courseSlug,
          pathSlug: learningPathSlug,
        }),
        allLearningPathsHubPart,
      ),
      {
        replace: true,
        state: { ...prevLocationState },
      },
    );
  }
};
